import { sendMessage, uploadAttachment } from '../api/client';
import { encodeImageAttachmentPayload, encodeTextPayload } from '../util/chatPayload';
import {
  processImageForUpload,
  releaseProcessedImageUpload,
} from '../util/processImageForSend';
import { randomUuid } from '../util/randomUuid';
import type { IncomingShare } from './incomingShare';

/**
 * Отправка того, чем поделились извне, в выбранный чат (стори 8.7).
 *
 * <p><b>Почему не через экран чата.</b> Отправка в `ChatScreen` завязана на его ленту:
 * оптимистичный бабл, полоса прогресса, состояние «отправляется». Здесь чат ещё не открыт —
 * человек выбрал его в списке, — и тащить сюда половину экрана ради этого значило бы завести
 * второй конвейер отправки. Общее у обоих путей то, что и должно быть общим: кодирование
 * полезной нагрузки (`chatPayload`), подготовка снимка (`processImageForSend`) и заливка
 * (`uploadAttachment`). Ленту чата дорисует сам экран, когда мы на него перейдём.
 */

export type ShareDeliveryProgress = {
  /** Сколько сообщений уже ушло. */
  sent: number;
  /** Сколько уйдёт всего: снимки плюс, если он есть, текст. */
  total: number;
};

/**
 * Отправляет содержимое в чат по одному сообщению за раз.
 *
 * <p>Порядок — снимки, потом текст: подпись под фотографиями читается как их продолжение, а не
 * как заголовок к тому, что ещё не пришло.
 *
 * <p>Ошибка на любом шаге прерывает отправку и выбрасывается наружу: молча отправить половину и
 * отчитаться успехом — худшее, что здесь можно сделать. Уже ушедшие сообщения остаются в чате,
 * и человек видит, на чём всё встало.
 */
export async function deliverShareToChat(
  chatId: string,
  share: IncomingShare,
  onProgress?: (progress: ShareDeliveryProgress) => void,
): Promise<void> {
  const total = share.files.length + (share.text ? 1 : 0);
  let sent = 0;
  const report = () => onProgress?.({ sent, total });
  report();

  // Пачка снимков показывается сеткой — той же, что и при обычной отправке из галереи.
  const albumId = share.files.length > 1 ? randomUuid() : null;

  for (const file of share.files) {
    const processed = await processImageForUpload(file.uri, file.widthPx ?? 0, file.heightPx ?? 0);
    try {
      const ext =
        processed.mimeType === 'image/webp' ? 'webp' : processed.mimeType === 'image/png' ? 'png' : 'jpg';
      const meta = await uploadAttachment(
        chatId,
        {
          kind: 'uri',
          uri: processed.fullUri,
          filename: `shared-${Date.now()}.${ext}`,
          mimeType: processed.mimeType,
        },
        { mimeType: processed.mimeType, thumbnailBase64: processed.thumbnailBase64 },
      );
      await sendMessage(
        chatId,
        encodeImageAttachmentPayload({
          mimeType: processed.mimeType,
          attachmentId: meta.id,
          sizeBytes: meta.sizeBytes,
          thumbnailBase64: processed.thumbnailBase64,
          albumId,
        }),
      );
    } finally {
      await releaseProcessedImageUpload(processed);
    }
    sent += 1;
    report();
  }

  if (share.text) {
    await sendMessage(chatId, encodeTextPayload(share.text));
    sent += 1;
    report();
  }
}
